import React, { useState } from 'react';
import { Loader, Modal, Pagination } from '@mantine/core';
import type { AdminKitchenPrintLogView } from '../../../services/admin-kitchen-prints/admin-kitchen-prints.service';
import { formatDateTime } from '../monitoring-page.utils';
import classes from '../monitoring-page.module.scss';

type KitchenPrintsTableProps = {
  rows: AdminKitchenPrintLogView[];
  isLoading: boolean;
  hasLoaded: boolean;
  totalPages: number;
  page: number;
  setPage: (value: number) => void;
};

const getStatusClassName = (status: string): string => {
  if (status === 'printed') {
    return classes.kitchenPrintStatusPrinted;
  }
  if (status === 'failed') {
    return classes.kitchenPrintStatusFailed;
  }
  return classes.kitchenPrintStatusQueued;
};

export const KitchenPrintsTable: React.FC<KitchenPrintsTableProps> = ({
  rows,
  isLoading,
  hasLoaded,
  totalPages,
  page,
  setPage,
}) => {
  const [previewTarget, setPreviewTarget] = useState<AdminKitchenPrintLogView | null>(null);

  if (isLoading && !hasLoaded) {
    return (
      <div className={classes.loadingWrap}>
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div className={classes.tableWrap}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th>{'תמונה'}</th>
              <th>{'חברה'}</th>
              <th>{'משמרת'}</th>
              <th>{'הזמנה'}</th>
              <th>{'שעת הזמנה'}</th>
              <th>{'מדפסת'}</th>
              <th>{'הודפס עי'}</th>
              <th>{'שעת הדפסה'}</th>
              <th>{'סטטוס'}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className={classes.clickableRow}
                onClick={() => setPreviewTarget(row)}
                data-testid={`monitoring-kitchen-row-${row.id}`}
              >
                <td>
                  <div className={classes.kitchenThumb}>
                    {row.printImageUrl ? (
                      <img
                        src={row.printImageUrl}
                        alt={'Kitchen print thumbnail'}
                        className={classes.kitchenThumbImage}
                      />
                    ) : (
                      <span>{'-'}</span>
                    )}
                  </div>
                </td>
                <td>{row.companyName}</td>
                <td>{row.sessionName}</td>
                <td>{row.orderCode ?? '-'}</td>
                <td>{formatDateTime(row.orderCreatedAt)}</td>
                <td>{row.printerName}</td>
                <td>{row.printedByName ?? '-'}</td>
                <td>{formatDateTime(row.printedAt)}</td>
                <td>
                  <span className={`${classes.kitchenPrintStatus} ${getStatusClassName(row.status)}`}>
                    {row.status}
                  </span>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={9} className={classes.empty}>
                  {'אין הדפסות מטבח להצגה.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className={classes.pagination}>
            <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
          </div>
        )}
      </div>

      <Modal
        opened={Boolean(previewTarget)}
        onClose={() => setPreviewTarget(null)}
        title={'תצוגת הדפסת מטבח'}
        centered
        size="xl"
        closeButtonProps={{ 'data-testid': 'monitoring-kitchen-preview-close' } as Record<string, string>}
      >
        {!previewTarget?.printImageUrl && (
          <p className={classes.error}>{'לא נמצאה תמונת הדפסה להצגה.'}</p>
        )}
        {previewTarget?.printImageUrl && (
          <img
            src={previewTarget.printImageUrl}
            alt={'Kitchen print preview'}
            className={classes.kitchenPrintPreviewImage}
          />
        )}
      </Modal>
    </>
  );
};
